// 20 Solar Energy themed planets — each with a unique visual style & shader approach
// Used by SolarLabPage and SolarPlanetRenderer

export const solarPlanets = [
  {
    id: "solar-panel",
    name: "Photon Grid",
    subtitle: "Solar Panel Array",
    description:
      "A planet surfaced entirely in photovoltaic cells. Rotating hexagonal panels track an orbiting sun, casting geometric shadows across silicon valleys.",
    shader: "solarPanel",
    color: "#1a3a6b",
    accent: "#4fc3f7",
    category: "Hardware",
  },
  {
    id: "solar-car",
    name: "Helios Racer",
    subtitle: "Solar-Powered Vehicle",
    description:
      "Streamlined world of aerodynamic curves and solar film. Energy flows like veins across the surface, powering perpetual motion.",
    shader: "solarCar",
    color: "#ff6d00",
    accent: "#ffd54f",
    category: "Engineering",
  },
  {
    id: "solar-farm",
    name: "Harvest Sun",
    subtitle: "Utility-Scale Solar Farm",
    description:
      "Endless rows of tilting mirrors across desert plains. The land itself hums with captured photons, a golden ocean of clean power.",
    shader: "solarFarm",
    color: "#f9a825",
    accent: "#fff176",
    category: "Infrastructure",
  },
  {
    id: "solar-cell",
    name: "Silicon Core",
    subtitle: "Photovoltaic Cell Research",
    description:
      "Dive into the crystalline lattice of a solar cell. Electron-hole pairs cascade through p-n junctions in a quantum dance of energy conversion.",
    shader: "solarCell",
    color: "#311b92",
    accent: "#b388ff",
    category: "Materials Science",
  },
  {
    id: "solar-thermal",
    name: "Molten Crown",
    subtitle: "Concentrated Solar Thermal",
    description:
      "A tower of mirrors focuses sunlight to a single blazing point. Molten salt rivers flow beneath the surface, storing heat for the night.",
    shader: "solarThermal",
    color: "#bf360c",
    accent: "#ff9e80",
    category: "Thermal Energy",
  },
  {
    id: "solar-satellite",
    name: "Orbital Collector",
    subtitle: "Space-Based Solar Power",
    description:
      "A megastructure in geostationary orbit beams microwave energy to Earth. Solar sails unfurl like metallic petals in the vacuum of space.",
    shader: "solarSatellite",
    color: "#0d47a1",
    accent: "#82b1ff",
    category: "Aerospace",
  },
  {
    id: "solar-ai",
    name: "Neural Sun",
    subtitle: "AI-Optimized Solar Grids",
    description:
      "Machine learning algorithms predict cloud cover, optimize panel angles, and balance grid loads. A digital brain powered by the star it studies.",
    shader: "solarAI",
    color: "#00c853",
    accent: "#69f0ae",
    category: "Computer Science",
  },
  {
    id: "solar-battery",
    name: "Ion Vault",
    subtitle: "Solar Energy Storage",
    description:
      "Lithium ions migrate through layered cathodes, storing daylight for darkness. A pulsing crystalline world of charge and discharge cycles.",
    shader: "solarBattery",
    color: "#00bfa5",
    accent: "#a7ffeb",
    category: "Energy Storage",
  },
  {
    id: "solar-blockchain",
    name: "Proof of Light",
    subtitle: "Solar-Powered Blockchain",
    description:
      "Decentralized energy trading on a blockchain validated by solar proof-of-work. Transparent ledgers glow with every kilowatt exchanged.",
    shader: "solarBlockchain",
    color: "#6200ea",
    accent: "#ea80fc",
    category: "Computer Science",
  },
  {
    id: "solar-desalination",
    name: "Aqua Prism",
    subtitle: "Solar Desalination",
    description:
      "Sunlight evaporates seawater through fractal membrane arrays. Fresh water condenses on cooled surfaces — the sun turns oceans into rain.",
    shader: "solarDesalination",
    color: "#006064",
    accent: "#80deea",
    category: "Water Tech",
  },
  {
    id: "solar-greenhouse",
    name: "Chloro Dome",
    subtitle: "Solar-Powered Agriculture",
    description:
      "Transparent photovoltaic glass lets specific light wavelengths pass while harvesting the rest. Plants and panels coexist in symbiotic architecture.",
    shader: "solarGreenhouse",
    color: "#1b5e20",
    accent: "#a5d6a7",
    category: "Agriculture",
  },
  {
    id: "solar-perovskite",
    name: "Crystal Lattice",
    subtitle: "Perovskite Solar Research",
    description:
      "Next-gen perovskite crystals promise cheaper, flexible solar cells. The ABX3 lattice structure shimmers with quantum efficiency breakthroughs.",
    shader: "solarPerovskite",
    color: "#880e4f",
    accent: "#f48fb1",
    category: "Materials Science",
  },
  {
    id: "solar-drone",
    name: "Sky Harvester",
    subtitle: "Solar-Powered UAV",
    description:
      "Autonomous drones with gossamer solar wings patrol above the clouds. Perpetual flight fueled by unfiltered sunlight at altitude.",
    shader: "solarDrone",
    color: "#37474f",
    accent: "#b0bec5",
    category: "Aerospace",
  },
  {
    id: "solar-paint",
    name: "Quantum Coat",
    subtitle: "Solar Paint Technology",
    description:
      "Every surface becomes a power source. Quantum dot nanoparticles suspended in paint absorb photons and generate current on any substrate.",
    shader: "solarPaint",
    color: "#e65100",
    accent: "#ffcc02",
    category: "Nanotechnology",
  },
  {
    id: "solar-grid",
    name: "Mesh Pulse",
    subtitle: "Smart Solar Microgrid",
    description:
      "An interconnected web of solar nodes, batteries, and consumers. Power flows like data packets through an intelligent energy internet.",
    shader: "solarGrid",
    color: "#1565c0",
    accent: "#90caf9",
    category: "Electrical Engineering",
  },
  {
    id: "solar-roadway",
    name: "Photo Road",
    subtitle: "Solar Roadway System",
    description:
      "Tempered glass hexagons embedded with LEDs and solar cells pave the highways. Roads that power themselves and the vehicles above them.",
    shader: "solarRoadway",
    color: "#455a64",
    accent: "#ffab40",
    category: "Civil Engineering",
  },
  {
    id: "solar-window",
    name: "Lucent Pane",
    subtitle: "Transparent Solar Glass",
    description:
      "Organic photovoltaic films turn windows into invisible power plants. Skyscrapers become vertical solar farms, harvesting light through transparency.",
    shader: "solarWindow",
    color: "#4a148c",
    accent: "#ce93d8",
    category: "Architecture",
  },
  {
    id: "solar-hydrogen",
    name: "H2 Forge",
    subtitle: "Solar Hydrogen Production",
    description:
      "Photoelectrolysis splits water molecules using sunlight. Green hydrogen bubbles up through catalytic membranes — the fuel of tomorrow.",
    shader: "solarHydrogen",
    color: "#01579b",
    accent: "#4dd0e1",
    category: "Chemistry",
  },
  {
    id: "solar-wearable",
    name: "Fiber Sun",
    subtitle: "Solar-Powered Wearables",
    description:
      "Photovoltaic fibers woven into fabric harvest ambient light. Your clothing charges your devices — fashion meets function at the nanoscale.",
    shader: "solarWearable",
    color: "#ad1457",
    accent: "#f06292",
    category: "Wearable Tech",
  },
  {
    id: "solar-deep-learning",
    name: "Helio Net",
    subtitle: "Deep Learning for Solar Forecasting",
    description:
      "Convolutional neural networks analyze satellite imagery to predict solar irradiance. Gradient descent through layers of atmospheric data.",
    shader: "solarDeepLearning",
    color: "#263238",
    accent: "#80cbc4",
    category: "Computer Science",
  },
];
